// ==========================================================================
// Spray Canvas  —  Ambulation Zone interactive feature
// An interactive graffiti wall over the brickwall texture: drag to spray
// grainy paint with occasional drips, swap colors from a skeuomorphic
// spray-can palette, and Clear wipes the wall + stamps a bullethole decal
// where you last sprayed. Raw HTML5 canvas (no p5), fully self-contained.
// ==========================================================================
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import bullethole from "../../../images/bullethole.png";
import { rafThrottle, prefersReducedMotion } from "../_shared/sketchFrame";
import "./spraycanvas.css";

// Canvas pixel buffer — matches the generous .spray-shell footprint in
// spraycanvas.css (560 x 420) so the wall is drawn 1:1 with no CSS stretch.
// getPoint() rescales pointer coords for any further CSS down-scale on narrow
// screens (the shell is max-width: 92vw).
const W = 560;
const H = 420;

const COLORS = ["#ff1900", "#12fe8d", "#75f8ff", "#ed77ff", "#fefa12", "#000", "#fff"];

function SprayCanvas(props) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const isDown = useRef(false);
    const lastPt = useRef({ x: W / 2, y: H / 2 });
    const bulletImg = useRef(null);
    // honor OS reduced-motion: drips are the only motion-flavored flourish here
    const reduceMotion = useRef(false);
    const [color, setColor] = useState("#ff1900");

    // grab the 2d context + preload the bullethole decal once on mount
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        ctxRef.current = canvas.getContext("2d");

        const img = new Image();
        img.src = bullethole;
        bulletImg.current = img;

        reduceMotion.current = prefersReducedMotion();
    }, []);

    // pull the active color out of a ref so the closure always sees latest
    const colorRef = useRef(color);
    useEffect(() => {
        colorRef.current = color;
    }, [color]);

    // translate a pointer event into canvas-local coords (mouse + touch)
    function getPoint(e) {
        const rect = canvasRef.current.getBoundingClientRect();
        let clientX;
        let clientY;
        if (e.touches && e.touches.length) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }
        // account for CSS scaling (canvas is 560x420 but max-width 92vw)
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    }

    // the grainy spray stamp — a DENSE burst of scattered dots + rare drip.
    // Thicker than a fine mist: more dots, bigger grains, heavier build-up so a
    // single pass lays real paint down.
    function spray(x, y) {
        const ctx = ctxRef.current;
        if (!ctx) return;
        const paint = colorRef.current;
        ctx.fillStyle = paint;

        for (let i = 0; i < 46; i++) {
            const r = Math.random() * 18;
            const a = Math.random() * Math.PI * 2;
            const dx = Math.cos(a) * r;
            const dy = Math.sin(a) * r;
            ctx.globalAlpha = 0.1 + Math.random() * 0.2;
            ctx.beginPath();
            ctx.arc(x + dx, y + dy, 0.9 + Math.random() * 3, 0, Math.PI * 2);
            ctx.fill();
        }

        // ~5% chance to start a drip: a short stack of fading dots running down
        // (suppressed under prefers-reduced-motion)
        if (!reduceMotion.current && Math.random() < 0.05) {
            const dripX = x + (Math.random() * 8 - 4);
            const len = 12 + Math.random() * 26;
            for (let d = 0; d < len; d++) {
                ctx.globalAlpha = Math.max(0, 0.22 - d * 0.006);
                ctx.beginPath();
                ctx.arc(dripX, y + d, 2.2 + Math.random() * 1, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.globalAlpha = 1;
        lastPt.current = { x, y };
    }

    // Coalesce drag spraying to one paint per animation frame. A bare canvas
    // mousemove can fire far more than 60x/sec and each spray() lays down 28+
    // arcs, so without this the drag is doing redundant work between repaints.
    // rafThrottle always replays with the LATEST point it received.
    const sprayThrottled = useRef(null);
    if (!sprayThrottled.current) {
        sprayThrottled.current = rafThrottle((x, y) => spray(x, y));
    }

    // cancel any pending throttled frame when the component goes away
    useEffect(() => {
        return () => {
            if (sprayThrottled.current) sprayThrottled.current.cancel();
        };
    }, []);

    function handleDown(e) {
        isDown.current = true;
        // paint the first dab immediately so a tap/click registers this frame
        const p = getPoint(e);
        spray(p.x, p.y);
    }

    function handleMove(e) {
        if (!isDown.current) return;
        const p = getPoint(e);
        sprayThrottled.current(p.x, p.y);
    }

    function handleUp() {
        isDown.current = false;
        // flush nothing further — drop any frame queued after the last move
        if (sprayThrottled.current) sprayThrottled.current.cancel();
    }

    function handleTouchMove(e) {
        if (!isDown.current) return;
        const p = getPoint(e);
        sprayThrottled.current(p.x, p.y);
    }

    // wipe the whole wall, then stamp a bullethole where you last sprayed
    function handleClear() {
        const ctx = ctxRef.current;
        if (!ctx) return;
        ctx.clearRect(0, 0, W, H);
        const img = bulletImg.current;
        const size = 120;
        const { x, y } = lastPt.current;
        const drawIt = () => {
            ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
        };
        if (img && img.complete && img.naturalWidth) {
            drawIt();
        } else if (img) {
            img.onload = drawIt;
        }
    }

    return (
        <div className="spray-shell">
            <canvas
                ref={canvasRef}
                width={W}
                height={H}
                className="spray-canvas"
                onMouseDown={handleDown}
                onMouseMove={handleMove}
                onMouseUp={handleUp}
                onMouseLeave={handleUp}
                onTouchStart={handleDown}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleUp}
            />
            <div className="spray-toolbar">
                {COLORS.map((c) => (
                    <button
                        key={c}
                        type="button"
                        aria-label={"spray color " + c}
                        className={"spray-can" + (c === color ? " active" : "")}
                        style={{ background: c }}
                        onClick={() => setColor(c)}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            setColor(c);
                        }}
                    />
                ))}
                <button
                    type="button"
                    className="spray-clear"
                    onClick={handleClear}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        handleClear();
                    }}
                >
                    CLEAR
                </button>
            </div>
        </div>
    );
}

export default SprayCanvas;
